import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Shield,
  AlertTriangle,
  Phone,
  Mail,
  ExternalLink,
  Users,
  Eye,
  Lock,
  Heart,
  ArrowLeft,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";

export default function SafetyGuidelines() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navbar />

      <div className="container flex justify-between items-center h-12">
        <Link href="/browse">
          <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </a>
        </Link>
      </div>

      <main className="container py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
            <Shield className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Safety Guidelines</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your safety is our top priority. Learn how we protect our community and what you can do 
            to stay safe while using Listo.
          </p>
        </div>

        {/* Zero Tolerance Policy */}
        <section className="card-elegant p-8 mb-8 border-destructive/30">
          <div className="flex items-start gap-4">
            <div className="bg-destructive/10 rounded-full p-3">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-2">Zero Tolerance Policy</h2>
              <p className="text-muted-foreground mb-4">
                Listo has a <strong>zero tolerance policy</strong> for human trafficking, child 
                exploitation, and any form of illegal activity. We actively monitor our platform 
                and work closely with law enforcement agencies to investigate and prosecute violators.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  All content is screened using advanced detection systems
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  Personal connection ads require wallet verification
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  Suspicious activity is reported to authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  Violators are permanently banned and may face prosecution
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Our Safety Measures */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <Lock className="w-6 h-6 text-primary" />
            How We Protect You
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-elegant p-6">
              <Eye className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Content Moderation</h3>
              <p className="text-sm text-muted-foreground">
                Every listing is scanned using AI and human review to detect suspicious patterns, 
                code words, and harmful content before it goes live.
              </p>
            </div>
            
            <div className="card-elegant p-6">
              <Users className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Identity Verification</h3>
              <p className="text-sm text-muted-foreground">
                Wallet verification adds an extra layer of accountability for personal connection 
                ads, helping ensure users are who they claim to be.
              </p>
            </div>
            
            <div className="card-elegant p-6">
              <AlertTriangle className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Report System</h3>
              <p className="text-sm text-muted-foreground">
                Our community-powered reporting system allows anyone to flag suspicious content. 
                Critical reports are reviewed within hours.
              </p>
            </div>
            
            <div className="card-elegant p-6">
              <Shield className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Law Enforcement Partnership</h3>
              <p className="text-sm text-muted-foreground">
                We maintain active partnerships with FBI, NCMEC, and local law enforcement to 
                investigate and prosecute illegal activity.
              </p>
            </div>
          </div>
        </section>

        {/* User Safety Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-secondary" />
            Safety Tips for Users
          </h2>
          <div className="card-elegant p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-4 text-primary">When Meeting in Person</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    Always meet in a public, well-lit location
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    Tell a friend or family member where you're going
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    Bring your phone and keep it charged
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">4.</span>
                    Trust your instincts - if something feels wrong, leave
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">5.</span>
                    Consider meeting at a police station "safe exchange zone"
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-4 text-secondary">Protecting Your Information</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">1.</span>
                    Never share your home address until you trust someone
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">2.</span>
                    Don't share banking, SSN, or credit card information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">3.</span>
                    Be wary of requests for gift cards or wire transfers
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">4.</span>
                    Use Listo's messaging system for initial contact
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary font-bold">5.</span>
                    Report any requests that seem unusual or coercive
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-destructive" />
            Red Flags to Watch For
          </h2>
          <div className="card-elegant p-8 border-destructive/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 text-destructive">In Listings</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Vague descriptions with suggestive language</li>
                  <li>• Prices that seem too good to be true</li>
                  <li>• Requests to communicate off-platform</li>
                  <li>• Reluctance to meet in public or show the item</li>
                  <li>• Stock photos or stolen images</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 text-destructive">Signs of Trafficking</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Person seems controlled by someone else</li>
                  <li>• Signs of physical abuse or malnourishment</li>
                  <li>• Scripted or inconsistent responses</li>
                  <li>• Unable to speak freely or alone</li>
                  <li>• Working excessively long hours</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Report Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <Phone className="w-6 h-6 text-primary" />
            How to Report
          </h2>
          <div className="card-elegant p-8 bg-gradient-to-br from-primary/5 to-secondary/5">
            <p className="text-muted-foreground mb-6">
              If you see something suspicious, <strong>say something</strong>. Your report could save a life.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-background rounded-lg">
                <div className="font-semibold mb-2">On Listo</div>
                <p className="text-sm text-muted-foreground mb-3">
                  Use the "Report" button on any listing
                </p>
                <Button size="sm">Report Content</Button>
              </div>
              
              <div className="text-center p-4 bg-background rounded-lg">
                <div className="font-semibold mb-2">Human Trafficking</div>
                <p className="text-sm text-muted-foreground mb-3">
                  National Human Trafficking Hotline
                </p>
                <a 
                  href="tel:1-888-373-7888" 
                  className="text-primary font-bold text-lg hover:underline"
                >
                  1-888-373-7888
                </a>
              </div>
              
              <div className="text-center p-4 bg-background rounded-lg">
                <div className="font-semibold mb-2">Child Exploitation</div>
                <p className="text-sm text-muted-foreground mb-3">
                  NCMEC CyberTipline
                </p>
                <a 
                  href="tel:1-800-843-5678" 
                  className="text-primary font-bold text-lg hover:underline"
                >
                  1-800-843-5678
                </a>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-4 justify-center">
              <Button variant="outline" asChild>
                <a 
                  href="https://www.missingkids.org/gethelpnow/cybertipline" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Report to NCMEC Online
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a 
                  href="https://humantraffickinghotline.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Trafficking Hotline Website
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a 
                  href="mailto:safety@listo.app"
                  className="flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email Our Safety Team
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Emergency Contact */}
        <section className="text-center">
          <div className="inline-block bg-destructive/10 border border-destructive/30 rounded-xl p-8">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">In Immediate Danger?</h2>
            <p className="text-muted-foreground mb-4">
              If you or someone else is in immediate danger, call emergency services.
            </p>
            <a 
              href="tel:911" 
              className="text-4xl font-bold text-destructive hover:underline"
            >
              Call 911
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12 py-8">
        <div className="container text-center text-sm text-muted-foreground">
          <p>
            Listo is committed to fighting human trafficking and protecting children. 
            We cooperate fully with law enforcement and will pursue legal action against violators.
          </p>
          <p className="mt-2">
            Last updated: January 2026
          </p>
        </div>
      </footer>
    </div>
  );
}
